import { acceptHMRUpdate, defineStore } from 'pinia'
import { api } from '@/api/client'
import { useConsoleStore } from './console'
import { usePlanStore } from './plan'
import { STATE_ORDER, worstState, stateLabel } from '@/utils/rules'
import { usd } from '@/utils/format'

/**
 * 风控状态机（01 策略与风控说明书 第 7.2 / 7.3 节）。
 *
 * 判定输入全部来自 console（交易所事实）与 plan（人工决策），
 * 本 store 只负责按规则推导状态，不自己持有业务数据。
 *
 * 两条不可让步的规则：
 *   · PROTECT / STOPPED 不会自行恢复，必须人工处置。
 *   · new_buy_allowed 是 fail closed：任一条件不满足就禁止新增买入。
 */
const EMPTY = {
  bot_state: 'NORMAL', running: false, latched: false,
  latched_at: 0, reason: '', audit: [],
}

export const useRiskStore = defineStore('risk', {
  state: () => ({
    busy: false,
  }),

  getters: {
    /* 运行状态全部来自后端快照。
     *
     * 原来这些是本地 state：刷新页面就回到"未启动"，换个浏览器又是另一套。
     * 最严重的是**「保护」态的锁定会被刷新清掉** —— 规则书要求
     * PROTECT/STOPPED 进入后必须人工确认处置才能恢复，一次 F5 就解除，
     * 那条规则等于不存在。 */
    raw() {
      return useConsoleStore().riskState ?? EMPTY
    },
    botState() {
      return this.raw.bot_state ?? 'NORMAL'
    },
    running() {
      return !!this.raw.running
    },
    latched() {
      return !!this.raw.latched
    },
    latchedAt() {
      return this.raw.latched_at ?? 0
    },
    reason() {
      return this.raw.reason ?? ''
    },
    audit() {
      return this.raw.audit ?? []
    },

    /** 净盈亏的有效输入。演示模式优先，用于在没有真实仓位时演练风控。 */
    pnlInput() {
      const plan = usePlanStore()
      const c = useConsoleStore()
      if (plan.demo.on) {
        return { value: plan.demo.pnl, available: true, source: 'demo' }
      }
      return {
        value: c.dailyPnlNet,
        available: c.pnlTrustworthy,
        source: 'ledger',
      }
    },

    currentLoss() {
      const { value, available } = this.pnlInput
      return available ? Math.max(0, -value) : 0
    },

    /** 距硬止损线的占比，用于进度条 */
    lossRatio() {
      const plan = usePlanStore()
      return Math.min(1, this.currentLoss / (plan.limits.stop || 1))
    },

    newBuyAllowed() {
      const c = useConsoleStore()
      const plan = usePlanStore()
      return (
        this.running &&
        this.botState === 'NORMAL' &&
        plan.usable &&
        c.connected &&
        this.pnlInput.available &&
        // 跨日闸门只挡新增买入。卖出与受控平仓永远放行 ——
        // 任何时候都不能把退出的路也堵上。
        !c.gateBlocksBuy &&
        // 棘轮收工同理：只挡买入。注意它**不参与下面的状态机判定** ——
        // 收工是止盈离场不是风控事件，写进状态机会变成每个赚钱的日子
        // 结束时都要人来按一次解除。
        !c.ratchetClosed
      )
    },

    /** 风控引擎检查项（01 第四章 + 第九章） */
    checks() {
      const c = useConsoleStore()
      const plan = usePlanStore()
      return [
        ['计划有效性', '价格位于批准低吸区间且计划未过期', plan.usable],
        ['盈亏可计量', '净盈亏有可信输入，风险线才有意义', this.pnlInput.available],
        ['仓位状态', '机器人处于「正常」且允许新增买入', this.botState === 'NORMAL'],
        ['盘口质量', '买卖价差与深度正常，无跳价异常', c.spreadBps > 0 && c.spreadBps < c.maxSpreadBps],
        ['可平仓性', '当前深度能吃下持仓，否则无法按估算价离场', c.unfillableQty <= 0],
        // 单日三条线管"今天别亏太多"，这条管"别慢慢失血"，两套独立
        ['跨日闸门', '周累计、连续亏损日均未触发', !c.gateBlocksBuy],
        // 收工是好事不是故障，所以这条在未启动与已锁定时都算通过，
        // 只有真正收工后才标红 —— 它回答的是"现在还能不能加仓"
        ['保底棘轮', '当日未因回落触及保底线而收工', !c.ratchetClosed],
        // 判据是**链路是否还活着**，不是"最近有没有成交"。
        // 原来用行情数据年龄：市场安静时链路好好的，这个数照样一路涨，
        // 于是在冷清的盘口上长期飘红 —— 量错了东西。
        // 行情数据本身要不要新鲜由档案决定（生产要、沙盒不要），
        // 这条判定已经并进后端的 stream_healthy。
        ['行情未断流', '行情链路持续有数据往来，静默即暂停', c.streamHealthy],
        // 补上时钟：这一项的描述一直写着"时钟漂移在容忍范围内"，
        // 但条件里从来没有查过时钟 —— 承诺了一件它不做的事。
        [
          '接口与时钟',
          '接口健康、时钟漂移在容忍范围内',
          c.apiHealth === 'OK' && c.connected &&
            Math.abs(c.clockDriftMs) <= c.maxClockSkewMs,
        ],
        ['订单可确认', '不存在状态未知的订单', !c.openOrders.some((o) => o.status === 'UNKNOWN')],
      ].map(([name, desc, pass]) => ({ name, desc, pass: !!pass }))
    },

    /** 检查项是否全通过 —— new_buy_allowed 的判定基础 */
    checksAllPass() {
      return this.checks.every((x) => x.pass)
    },
  },

  actions: {
    /** 所有写操作走同一条路：调后端 → 重拉快照，不在前端猜结果 */
    async _act(fn) {
      this.busy = true
      try {
        await fn()
        await useConsoleStore().pull()
      } finally {
        this.busy = false
      }
    },

    log(action, reason) {
      return this._act(() => api.riskLog(action, reason || ''))
    },

    /** 每次数据刷新后重算状态，并把结论推给后端 */
    async evaluate() {
      const c = useConsoleStore()
      const plan = usePlanStore()

      // 锁定态不自行恢复。这里先挡一道，后端还会再挡一道 ——
      // 前端每两秒推一次判定，少了闸门行情一好转就把 PROTECT 冲掉。
      if (this.latched) return

      let next = 'NORMAL'
      let reason = ''

      /* 两层亏损线：本所一层、账户一层，取更严的那层。
       *
       * 阈值共用一组 —— $80,000 的预算本来就是账户级的，不是每家一份。
       * 只判本所那层时存在一条完整的绕过路径：在 A 亏到离保护线只差
       * 一点，切到 B，账本重建、当日盈亏归零、三条线回到起点，
       * 可以再亏满一整轮，而系统全程不会说不。
       */
      const layers = [
        { loss: this.currentLoss, ok: this.pnlInput.available, scope: '' },
        {
          loss: c.accountPnlTrustworthy ? Math.max(0, -c.accountDailyPnl) : 0,
          // 演示模式下账户级没有对应的演练输入，不参与判定
          ok: !plan.demo.on && c.accountPnlTrustworthy && c.multiExchangeToday,
          scope: '账户合计',
        },
      ]
      for (const { loss, ok, scope } of layers) {
        if (!ok) continue
        // 标出是哪一层触发的，否则事后看审计日志说不清为什么停
        const tag = scope ? `${scope}净亏损` : '净亏损'
        if (loss >= plan.limits.stop) {
          next = worstState(next, 'PROTECT')
          reason = `${tag} ${usd(loss, 0)}，已达「保护」阈值 ${usd(plan.limits.stop, 0)}`
        } else if (loss >= plan.limits.pause) {
          next = worstState(next, 'PAUSED')
          reason = `${tag} ${usd(loss, 0)}，已达「暂停」阈值 ${usd(plan.limits.pause, 0)}`
        } else if (loss >= plan.limits.observe) {
          next = worstState(next, 'OBSERVE')
          reason = `${tag} ${usd(loss, 0)}，已达「观察」阈值 ${usd(plan.limits.observe, 0)}`
        }
      }

      // 跨日闸门触发即进 PAUSED：周累计与连亏都是"该停下来想想了"，
      // 须人工书面确认才解除。月度回撤不在此列，它只降单档金额。
      if (c.gateBlocksBuy) {
        next = worstState(next, 'PAUSED')
        reason = c.gateReasons.join('；') || '跨日闸门触发'
      }

      // 系统异常直接进 PAUSED（01 第九章）
      if (!c.connected) {
        next = worstState(next, 'PAUSED')
        reason = '后端不可达'
      } else if (c.apiHealth !== 'OK') {
        next = worstState(next, 'PAUSED')
        reason = 'API 异常'
      } else if (!c.streamHealthy) {
        // 与检查项同源：断流的判定交给后端的 stream_healthy，
        // 前端不再自己拿行情年龄比一遍 —— 两处各判各的迟早会打架。
        next = worstState(next, 'PAUSED')
        reason = `行情链路异常：${c.streamReasons.join('；') || '未知原因'}`
      } else if (c.price && c.price < plan.limits.floorPrice) {
        next = worstState(next, 'PAUSED')
        reason = `现价 ${usd(c.price, 0)} 跌破最低人工决策价 ${usd(plan.limits.floorPrice, 0)}`
      }

      // 后端不可达时推不过去，也没必要推 —— 下一轮连上会补
      if (!c.connected) return
      if (next === this.botState && reason === this.reason) return
      try {
        await api.riskTransition(next, reason)
      } catch {
        /* 推送失败下一轮会重来，不打断界面 */
      }
    },

    start() {
      return this._act(() => api.riskStart())
    },

    pause(reason = '人工暂停') {
      return this._act(() => api.riskPause(reason))
    },

    protect(reason = '人工触发保护模式') {
      return this._act(() => api.riskProtect(reason))
    },

    /** 人工解除锁定：规则要求 PROTECT 后必须人工处置才能恢复 */
    release(reason) {
      return this._act(() => api.riskRelease(reason || '人工确认已处置'))
    },
  },
})

export { STATE_ORDER }

/* 让 store 支持热更新。
 *
 * 没有这一行时，改 store 文件 Vite 会重新求值模块，但**已实例化的 store
 * 仍持有旧的 getter 闭包** —— 页面看起来在热更新，实际执行的还是旧逻辑，
 * 只有整页刷新才生效。改文案时很容易误判成"改了没用"。 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRiskStore, import.meta.hot))
}
